import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { BottomNavService, DslService, MenuActionsService, MenuContentItem, NavigationService, UrlService, WINDOW } from '@frontend/vanilla/core';
import { BottomNavConfig } from '@frontend/vanilla/features/bottom-nav';
import { Observable, first, forkJoin, switchMap } from 'rxjs';

import { NavigationStack } from './navigation-stack';
import { NavigationStackHistoryService } from './navigation-stack-history.service';
import { getNavigationStackParameters } from './navigation-stack-parameters';
import { PredefinedStackNames } from './predefined-stack-names';
import { getActiveStackByUrl } from './utils';

@Injectable({
    providedIn: 'root',
})
export class BottomNavigationStacksService {
    private readonly window = inject(WINDOW);
    private readonly navigationService = inject(NavigationService);
    private readonly navigationStackHistoryService = inject(NavigationStackHistoryService);

    private bottomNavConfig = inject(BottomNavConfig);
    private destroyRef = inject(DestroyRef);
    private urlService = inject(UrlService);
    private menuActionsService = inject(MenuActionsService);
    private bottomNavService = inject(BottomNavService);
    private dslService = inject(DslService);
    private productUtils = inject(SportsProductUtils);

    private _isEnabled: boolean;
    private bottomNavStacks: NavigationStack[] = [];
    private homeStack = new NavigationStack();

    public init(): void {
        forkJoin([this.bottomNavConfig.whenReady, this.bottomNavService.whenReady])
            .pipe(
                switchMap(() => this.getVisibleBottomNavStacksItems()),
                first(),
            )
            .subscribe((items) => {
                const hasHomeStack = items.some((x) => x.name === PredefinedStackNames.Home);
                if (!hasHomeStack) {
                    return;
                }

                this.initVanillaHighlightToggle();
                this.initStacks(items);
                this.listenUrlChanges();
                this._isEnabled = true;
            });
    }

    public goBackStackScoped(): void {
        this.navigationStackHistoryService.goBackStackScoped();
    }

    public get isEnabled(): boolean {
        return this._isEnabled;
    }

    private initVanillaHighlightToggle(): void {
        this.bottomNavService.enableHighlighting = false;
        this.productUtils
            .fromSportsEnter()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.bottomNavService.enableHighlighting = false;
                const lastSportsStack = this.navigationStackHistoryService.lastSportStack;
                if (lastSportsStack) {
                    this.setActiveStack(lastSportsStack);
                }
            });
        this.productUtils
            .fromSportsLeave()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => (this.bottomNavService.enableHighlighting = true));
    }

    private initStacks(menuItems: MenuContentItem[]): void {
        const stacks = menuItems.map((x) => {
            const params = getNavigationStackParameters(x);
            params.userEntryUrl = params.userEntryUrl ? this.urlService.parse(params.userEntryUrl).pathname : undefined;

            return new NavigationStack({
                rootUrl: this.urlService.parse(x.url).pathname,
                name: x.name,
                clickAction: x.clickAction,
                parameters: params,
            });
        });

        const homeStack = stacks.find((x) => x.name === PredefinedStackNames.Home);

        if (!homeStack) {
            return;
        }

        stacks
            .filter((x) => !x.parameters.useCustomClickHandler)
            .forEach((stack) => {
                this.menuActionsService.register(stack.clickAction, () => this.onBottomNavItemClick(stack));
            });

        this.homeStack = homeStack;
        this.bottomNavStacks = stacks;

        const activeStack = this.getActiveByCurrentUrl();
        this.navigationStackHistoryService.init(this.urlService.current().url(), activeStack, this.homeStack.rootUrl);
        this.setActiveStack(activeStack);
    }

    private listenUrlChanges(): void {
        this.navigationStackHistoryService.historyChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((historyChange) => {
            if (PredefinedStackNames.NonSportsProduct === historyChange.stack?.name) {
                return;
            }

            const possibleStack = this.getActiveByCurrentUrl();
            if (!historyChange.stack || possibleStack?.parameters?.canBeActivatedByNav) {
                this.navigationStackHistoryService.setActiveStackAndUpdateCurrentHistory(possibleStack);
                this.bottomNavService.setActiveItem(possibleStack.name);
                return;
            }

            this.bottomNavService.setActiveItem(historyChange.stack.name);
        });
    }

    private getVisibleBottomNavStacksItems(): Observable<MenuContentItem[]> {
        const linkItems = this.bottomNavConfig.items.filter((x) => !!x.name && !!x.clickAction && !!x.url);
        return this.dslService.evaluateContent(linkItems);
    }

    private onBottomNavItemClick(navigationStack: NavigationStack): void {
        const currentUrl = this.urlService.current().url();

        const alreadyActive = navigationStack.name === this.navigationStackHistoryService.activeStack?.name;

        if (alreadyActive && currentUrl === navigationStack.userEntryUrl) {
            this.window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (alreadyActive) {
            this.navigationService.goTo(navigationStack.userEntryUrl);
            return;
        }

        this.setActiveStack(navigationStack);
        this.navigationService.goTo(navigationStack.currentUrl ?? navigationStack.userEntryUrl);
    }

    private getActiveByCurrentUrl(): NavigationStack {
        const stack = getActiveStackByUrl(this.urlService.current().pathname, this.homeStack.rootUrl, this.bottomNavStacks);
        return stack ?? this.homeStack;
    }

    private setActiveStack(active: NavigationStack): void {
        this.navigationStackHistoryService.setActiveStack(active);
        this.bottomNavService.setActiveItem(active.name);
    }
}
